import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { invalidateTruckShiftQueries } from 'src/hooks/invalidateTruckShiftQueries';

export const truckShiftRestPath = (id: number): string => `/api/v5/truck_shifts/${id}/rest`;

/** 休みの種類。AM / PM の時間帯は会社設定なので API が決める（FE は kind だけ送る） */
export type TruckShiftRestKind = 'ALL' | 'AM' | 'PM';

/**
 * 休みにする時間帯。次の 3 つの形のどれか。
 * - `{ date, kind }`: 終日 / 午前 / 午後。`date` は押したセルの業務日（JST の 'YYYY-MM-DD'）
 * - `{ date, start_time, end_time }`: その日の時刻だけで指定する形
 * - `{ start_at, end_at }`: 完全な ISO8601 で窓そのものを渡す形
 *
 * Why not `{ start_at, end_at }` に `date` も持たせる: 窓は日付を含んでいるので、
 * date を併せて渡すと「どちらが本当の日か」を API 側に決めさせることになる。
 * Why 時刻だけの形も残す: 稼働表など他の入口がまだ使う（API も受け続ける）。
 */
export type TruckShiftRestRequest =
  | { date: string; kind: TruckShiftRestKind }
  | { date: string; start_time: string; end_time: string }
  | { start_at: string; end_at: string };

export type TruckShiftRestResponse = {
  /** 休みを引いたあとに残った勤務（開始が早い順）。1 本も残らなければ空 */
  shifts: {
    id: number;
    start_at: string;
    end_at: string;
    working_available_duration_seconds: number | null;
  }[];
  deleted: boolean;
  /** 労働時間が残り勤務に収まらず、API 側で短くしたとき true */
  working_hours_shortened: boolean;
};

/**
 * 勤務から休みの時間帯を引く（長距離配車の「休みにする」）。
 * 勤務が 2 本に分かれる場合も API 側で 1 トランザクションで処理される。
 */
export const useMutationTruckShiftRest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      { id, rest }: { id: number; rest: TruckShiftRestRequest },
    ): Promise<TruckShiftRestResponse> => {
      const { data } = await axios.post<TruckShiftRestResponse>(truckShiftRestPath(id), { rest });
      return data;
    },

    onSuccess: () => { invalidateTruckShiftQueries(queryClient); }
  });
};
